import { Injectable } from '@angular/core';
import { createStore, select, setProps, withProps } from '@ngneat/elf';
import {
  withEntities,
  setEntities,
  selectAllEntities,
  updateAllEntities,
  selectManyByPredicate,
} from '@ngneat/elf-entities';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  take,
} from 'rxjs';

import {
  FormNavigationType,
  FormNavSectionItemType,
  FormNavSectionType,
  FormNavUpdate,
  FormNavUIEvents,
  SECTION_HEADER_STATUSES,
  SECTION_ITEM_STATUSES,
  SectionId,
  SubSectionId,
  DEFAULT_EXPRESS_SW_NAVBAR_STATE,
  DEFAULT_LONGFROM_SW_NAVBAR_STATE,
  DEFAULT_COR_NAVBAR_STATE,
  SubSectionClick,
  SectionClick,
  SECTION_DEPENDENCIES,
} from '@digital-platform/unified-wp-common-domain';
import { WasteProfileType } from '@digital-platform/unified-wp-waste-profile/domain';

export interface FormNavState {
  title: string;
  subTitle: string;
}

const navBarStore = createStore(
  { name: 'navBar' },
  withEntities<FormNavSectionType>(),
  withProps<FormNavState>({ title: '', subTitle: '' })
);

/**
 * Service to manage the state of the navigation bar
 */
@Injectable({ providedIn: 'root' })
export class NavBarService {
  public navBarSections$ = navBarStore.pipe(selectAllEntities());

  public navBarHeaderState$ = navBarStore.pipe(select((state) => state));

  // Observable of any click events to NavBar(clicking a subsection or section)
  public navBarEvents$ = new BehaviorSubject<FormNavUIEvents | null>(null);

  private getActiveSection$ = navBarStore.pipe(
    selectManyByPredicate((section) => section.isActive === true),
    map((sections) => sections[0]) // get the first active section
  );

  public activeSection$ = this.getActiveSection$;

  public activeSubSection$ = this.getActiveSection$.pipe(
    filter((section) => section !== undefined),
    map((section) =>
      section.children.find((subsection) => subsection.isActive === true)
    ),
    distinctUntilChanged((prev, curr) => prev?.id === curr?.id) // only emit when the active subsection changes
  );

  /**
   * Emits the next FormNavUIEvents (either SectionClick or SubSectionClick),
   * skipping over any locked subsections or sections.
   */
  private nextLocation$: Observable<FormNavUIEvents | null> = combineLatest([
    this.navBarSections$,
    this.activeSection$,
    this.activeSubSection$,
  ]).pipe(
    map(
      ([allSections, activeSection, activeSubSection]: [
        FormNavSectionType[],
        FormNavSectionType,
        FormNavSectionItemType | undefined
      ]): FormNavUIEvents | null => {
        // nothing to do if we don't have both an active section and subsection
        if (!activeSection || !activeSubSection) {
          return null;
        }

        // helpers to check if we are able to navigate to a section & subsection (can't if it's locked)
        const isSectionUnlocked = (sect: FormNavSectionType) =>
          sect.status !== SECTION_HEADER_STATUSES.LOCKED;
        const isSubSectionUnlocked = (sub: FormNavSectionItemType) =>
          sub.status !== SECTION_ITEM_STATUSES.LOCKED;

        // within the current active section, find all other subsections and the index of the active section
        const siblings: FormNavSectionItemType[] = activeSection.children;
        const currentIdx: number = siblings.findIndex(
          (ch: FormNavSectionItemType): boolean => ch.id === activeSubSection.id
        );

        // check to find next unlocked subsection in the same section
        const nextSub: FormNavSectionItemType | undefined = siblings
          .slice(currentIdx + 1)
          .find(isSubSectionUnlocked);
        if (nextSub) {
          return this.makeSubSectionClick(
            activeSection.id as SectionId,
            nextSub.id as SubSectionId
          );
        }

        // otherwise scan parent sections for the next unlocked parent section
        const currentSectIdx: number = allSections.findIndex(
          (s: FormNavSectionType): boolean => s.id === activeSection.id
        );
        for (let i: number = currentSectIdx + 1; i < allSections.length; i++) {
          const nextSect: FormNavSectionType = allSections[i];
          if (isSectionUnlocked(nextSect)) {
            return this.makeSectionClick(nextSect.id as SectionId);
          }
        }

        // nowhere left to go
        return null;
      }
    )
  );

  /** This is true if there is somewhere to navigate to next, or null if there is nowhere to go. */
  public hasNext$: Observable<boolean> = this.nextLocation$.pipe(
    map((evt) => evt !== null),
    distinctUntilChanged()
  );

  /**
   * Observable of the currently active Section and it's subsections
   */
  public activeSectionState$: Observable<
    Record<SubSectionId, FormNavSectionItemType> | undefined
  > = this.getActiveSection$.pipe(
    map((section) => section?.children),
    // filter out undefined subsections
    filter((subsections) => subsections !== undefined),
    map((subsections) =>
      // convert subsections to a map for easy access
      subsections?.reduce(
        (acc, subsection) => ({
          ...acc,
          [subsection.id]: subsection,
        }),
        {} as Record<SubSectionId, FormNavSectionItemType>
      )
    ),
    distinctUntilChanged()
  );

  private makeSubSectionClick(
    sectionId: SectionId,
    subSectionId: SubSectionId
  ): SubSectionClick {
    return { type: 'SubSectionClick', sectionId, subSectionId };
  }

  private makeSectionClick(sectionId: SectionId): SectionClick {
    return { type: 'SectionClick', sectionId };
  }

  /**
   * Any click events to NavBar(clicking a subsection or section) reported here, they are sent to any listener
   * @param event
   */
  public navBarClickEvents(event: FormNavUIEvents | null): void {
    this.navBarEvents$.next(event);
  }

  public set(state: FormNavigationType): void {
    navBarStore.update(
      setEntities(state.sections),
      setProps({ title: state.title, subTitle: state.subtitle })
    );
  }

  public updateNavBarState(update: FormNavUpdate): void {
    this.updateState(update);
  }

  /**
   *
   * @param sectionId Logic to pick the next subsection to be active when a section is clicked
   * @returns
   */
  private pickSubSection(
    sectionId: SectionId
  ): FormNavSectionItemType | undefined {
    const section = navBarStore.getValue().entities[sectionId];

    if (!section) return undefined;

    // if a subsection is not locked and not complete, then it is the next active subsection to be expanded
    return section.children.find(
      (subSection) =>
        subSection.status !== SECTION_ITEM_STATUSES.COMPLETE &&
        subSection.status !== SECTION_ITEM_STATUSES.LOCKED
    );
  }

  /**
   *
   * @param update Update the state of the navigation bar based on different update events
   */
  private updateState(update: FormNavUpdate): void {
    navBarStore.update(
      updateAllEntities((section) => ({
        ...section,
        isActive: this.sectionActiveStatus(update, section),
        children: section.children.map((subsection) => ({
          ...subsection,
          status: this.getSubSectionStatusFromUpdate(update, subsection),
          isActive: this.getSubSectionActiveStatusFromUpdate(
            update,
            subsection,
            section
          ),
        })),
        status: this.getSectionStatusFromUpdate(update, section),
      }))
    );
  }

  private sectionActiveStatus(
    update: FormNavUpdate,
    section: FormNavSectionType
  ): boolean {
    // A section is considered active if it is the clicked/interacted section and it is not locked
    switch (update.type) {
      case 'SectionClickUpdate':
        return (
          update.sectionId === section.id &&
          section.status !== SECTION_HEADER_STATUSES.LOCKED
        );
    }

    return section.isActive;
  }

  /**
   *
   * @param update Get the active status of a subsection based on the update event
   * @param subsection
   * @returns
   * todo: Need to figure out what to do when you open a completed section, got to expand all of them.
   */
  private getSubSectionActiveStatusFromUpdate(
    update: FormNavUpdate,
    subsection: FormNavSectionItemType,
    section?: FormNavSectionType
  ): boolean {
    // if the section is complete, then all subsections are considered active
    if (
      section &&
      this.getSectionStatusFromUpdate(update, section) ===
        SECTION_HEADER_STATUSES.COMPLETE
    ) {
      return true;
    }

    switch (update.type) {
      // When a new section is clicked, we need to pick the corresponding subsection which is going to be active
      case 'SectionClickUpdate': {
        // if section  is
        const pickedSubSection = this.pickSubSection(update.sectionId);

        // A subsection is considered active if it is the picked subsection and it is not locked
        return (
          subsection.id === pickedSubSection?.id &&
          subsection.status !== SECTION_ITEM_STATUSES.LOCKED
        );
      }
      case 'SubSectionClickUpdate':
      case 'SubSectionStatusUpdate':
        // Same as above
        return (
          subsection.id === update.subSectionId &&
          subsection.status !== SECTION_ITEM_STATUSES.LOCKED
        );
      case 'AccordionClickUpdate':
        if (update.accordionState.state === 'Expanded') {
          if (
            subsection.id === update.subSectionId &&
            subsection.status !== SECTION_ITEM_STATUSES.LOCKED
          ) {
            return true;
          }
          // if it is not the expanded subsection, then it should be collapsed/inactive
          return false;
        } else {
          // if the subsection is collapsed, then the active subsection should be made inactive
          if (
            subsection.id === update.subSectionId &&
            subsection.status !== SECTION_ITEM_STATUSES.LOCKED
          ) {
            return false;
          }
          // if this is not the interacted subsection, just keep the old state as is.
          return subsection.isActive;
        }
      case 'Recompute':
        // if the recompute event is triggered, we need to check if the subsection is complete and active
        // if the subsection is complete and active, then it is considered active
        return (
          section?.status === SECTION_HEADER_STATUSES.COMPLETE &&
          section.isActive === true
        );
    }
    return false;
  }

  private getSectionStatusFromUpdate(
    update: FormNavUpdate,
    section: FormNavSectionType
  ): keyof typeof SECTION_HEADER_STATUSES {
    // if it's locked, consider it locked
    if (section.status === SECTION_HEADER_STATUSES.LOCKED) {
      return SECTION_HEADER_STATUSES.LOCKED;
    }

    let hasInProgress = false;
    let completedCount = 0;
    for (const subSection of section.children) {
      // error
      const status = this.getSubSectionStatusFromUpdate(update, subSection);
      if (status === SECTION_ITEM_STATUSES.INCOMPLETE) {
        return SECTION_HEADER_STATUSES.INCOMPLETE;
      } else if (status === SECTION_ITEM_STATUSES.COMPLETE) {
        completedCount++;
      } else if (status === SECTION_ITEM_STATUSES.IN_PROGRESS) {
        hasInProgress = true;
      }
    }

    // if all sections are completed, then section is complete
    if (completedCount === section.children.length) {
      return SECTION_HEADER_STATUSES.COMPLETE;
    }

    // if atleast 1 section is in progress or if only some of the sections are complete, the section is in progress
    if (hasInProgress || completedCount > 0) {
      return SECTION_HEADER_STATUSES.IN_PROGRESS;
    }

    // above conditions are false
    return SECTION_HEADER_STATUSES.NOT_STARTED;
  }

  private getSubSectionStatusFromUpdate(
    update: FormNavUpdate,
    currentSubSection: FormNavSectionItemType
  ): keyof typeof SECTION_ITEM_STATUSES {
    const isActive = this.getSubSectionActiveStatusFromUpdate(
      update,
      currentSubSection
    );

    // if the subsection is valid and in preview mode, then it is complete
    if (
      update.contentStatus[currentSubSection.id]?.status === 'PreviewValid' ||
      update.contentStatus[currentSubSection.id]?.status === 'Valid'
    ) {
      return SECTION_ITEM_STATUSES.COMPLETE;
    }

    const dependentSection = SECTION_DEPENDENCIES[
      currentSubSection.id as SubSectionId
    ] as SubSectionId;
    const dependentSectionStatus =
      update.contentStatus[dependentSection]?.status;

    // if the subsection is empty and the dependent section is valid, then it is set to not started, else locked
    if (update.contentStatus[currentSubSection.id]?.status === 'Empty') {
      if (
        dependentSectionStatus === 'Valid' ||
        dependentSectionStatus === 'PreviewValid'
      ) {
        return SECTION_ITEM_STATUSES.NOT_STARTED;
      } else {
        return SECTION_ITEM_STATUSES.LOCKED;
      }
    }

    // if the subsection is valid and currently not active, then it stays as complete
    if (
      update.contentStatus[currentSubSection.id]?.status === 'Valid' &&
      !isActive
    ) {
      return SECTION_ITEM_STATUSES.COMPLETE;
    }

    // if the subsection has an error, then it is incomplete
    if (
      update.contentStatus[currentSubSection.id]?.status === 'Error' &&
      !isActive
    ) {
      return SECTION_ITEM_STATUSES.INCOMPLETE;
    }

    // if the subseciton is locked, then it is locked
    if (currentSubSection.status === SECTION_ITEM_STATUSES.LOCKED) {
      return SECTION_ITEM_STATUSES.LOCKED;
    }

    // currently active subsection is in progress
    if (isActive) {
      return SECTION_ITEM_STATUSES.IN_PROGRESS;
    }

    // otherwise, the subsection is not started
    return SECTION_HEADER_STATUSES.NOT_STARTED;
  }

  public reset(): void {
    navBarStore.reset();
  }

  // get the right navbar init state
  public getNavBarState(
    wasteProfileType: WasteProfileType
  ): FormNavigationType {
    switch (wasteProfileType) {
      case 'cor':
        return DEFAULT_COR_NAVBAR_STATE;
      case 'express':
        return DEFAULT_EXPRESS_SW_NAVBAR_STATE;
      case 'special-waste':
        return DEFAULT_LONGFROM_SW_NAVBAR_STATE;
      default:
        throw new Error('Invalid waste profile type');
    }
  }

  /**
   * “Fire” exactly one nextLocation event into the nav‐bar event stream.
   */
  public goNext(): void {
    this.nextLocation$
      .pipe(take(1))
      .subscribe((evt: FormNavUIEvents | null) => this.navBarClickEvents(evt));
  }
}
